import { Box, Button, Checkbox, Flex, Icon, Popover, Portal, Text } from "@chakra-ui/react";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useState } from "react";

const CHIP_VALUES = [1, 2, 3, 4];

const ROWS = [
    { key: "bedrooms", label: "Recámaras", shortLabel: "rec" },
    { key: "bathrooms", label: "Baños", shortLabel: "baños" },
    { key: "halfBathrooms", label: "Medios baños", shortLabel: "medios" },
    { key: "parkingLots", label: "Estacionamientos", shortLabel: "estac." },
];

const isExactValue = (value) => value.length === 2 && value[0] === value[1];

const buildDraftFromChip = (n, exact) => (exact ? [n, n] : [n]);

const buildPillLabel = (state) => {
    const bits = [];
    for (const row of ROWS) {
        const value = state[row.key];
        if (value.length === 0) continue;
        const n = value[0];
        bits.push(isExactValue(value) ? `${n} ${row.shortLabel}` : `${n}+ ${row.shortLabel}`);
    }
    if (bits.length === 0) return "Características";
    return bits.slice(0, 2).join(" · ");
};

const defaultExactMap = (s) => ({
    bedrooms: isExactValue(s.bedrooms),
    bathrooms: isExactValue(s.bathrooms),
    halfBathrooms: isExactValue(s.halfBathrooms),
    parkingLots: isExactValue(s.parkingLots),
});

const defaultDrafts = (s) => ({
    bedrooms: s.bedrooms,
    bathrooms: s.bathrooms,
    halfBathrooms: s.halfBathrooms,
    parkingLots: s.parkingLots,
});

const arraysEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
};

// Hook owns drafts + exact-mode flags. Exposes commit/reset for outer container.
export const useBedsBathsDraft = (state) => {
    const [drafts, setDrafts] = useState(() => defaultDrafts(state));
    const [exactMap, setExactMap] = useState(() => defaultExactMap(state));

    useEffect(() => {
        setDrafts(defaultDrafts(state));
        setExactMap(defaultExactMap(state));
    }, [state.bedrooms, state.bathrooms, state.halfBathrooms, state.parkingLots]);

    const isDirty = ROWS.some((row) => !arraysEqual(drafts[row.key], state[row.key]));

    const onPickAny = (key) => setDrafts((d) => ({ ...d, [key]: [] }));
    const onPickN = (key, n) =>
        setDrafts((d) => ({ ...d, [key]: buildDraftFromChip(n, exactMap[key]) }));
    const onToggleExact = (key, next) => {
        setExactMap((m) => ({ ...m, [key]: next }));
        setDrafts((d) => {
            const v = d[key];
            if (v.length === 0) return d;
            const n = v[0];
            return { ...d, [key]: next ? [n, n] : [n] };
        });
    };

    const commit = (setFilter, clearFilter) => {
        ROWS.forEach(({ key }) => {
            const v = drafts[key];
            if (v.length === 0) clearFilter(key);
            else setFilter(key, v);
        });
    };

    const reset = (_setFilter, clearFilter) => {
        setDrafts({ bedrooms: [], bathrooms: [], halfBathrooms: [], parkingLots: [] });
        setExactMap({
            bedrooms: false,
            bathrooms: false,
            halfBathrooms: false,
            parkingLots: false,
        });
        ROWS.forEach(({ key }) => clearFilter(key));
    };

    return {
        drafts,
        exactMap,
        onPickAny,
        onPickN,
        onToggleExact,
        isDirty,
        canCommit: true,
        commit,
        reset,
    };
};

const ChipRow = ({
    label,
    draftKey,
    value,
    committed,
    exact,
    onPickAny,
    onPickN,
    onToggleExact,
}) => {
    const selectedN = value.length > 0 ? value[0] : null;
    const showDot = !arraysEqual(value, committed);
    return (
        <Box mb={3}>
            <Text fontSize="sm" fontWeight={500} color="gray.700" mb={2}>
                {label}
                {showDot ? " •" : ""}
            </Text>
            <Flex gap={2}>
                <Button
                    onClick={() => onPickAny(draftKey)}
                    h="32px"
                    px={3}
                    fontSize="xs"
                    borderRadius="full"
                    borderWidth="1px"
                    borderColor={selectedN === null ? "colorPalette.solid" : "gray.200"}
                    bg={selectedN === null ? "colorPalette.solid" : "white"}
                    color={selectedN === null ? "white" : "gray.700"}
                    _hover={{ bg: selectedN === null ? "colorPalette.solid" : "gray.50" }}
                >
                    Todo
                </Button>
                {CHIP_VALUES.map((n) => {
                    const isOn = selectedN === n;
                    const chipLabel = exact ? `${n}` : `${n}+`;
                    return (
                        <Button
                            key={n}
                            onClick={() => onPickN(draftKey, n)}
                            h="32px"
                            minW="44px"
                            px={3}
                            fontSize="xs"
                            borderRadius="full"
                            borderWidth="1px"
                            borderColor={isOn ? "colorPalette.solid" : "gray.200"}
                            bg={isOn ? "colorPalette.solid" : "white"}
                            color={isOn ? "white" : "gray.700"}
                            _hover={{ bg: isOn ? "colorPalette.solid" : "gray.50" }}
                        >
                            {chipLabel}
                        </Button>
                    );
                })}
            </Flex>
            <Flex mt={2} align="center" gap={2}>
                <Checkbox.Root
                    checked={exact}
                    onCheckedChange={(d) => onToggleExact(draftKey, !!d.checked)}
                    size="sm"
                >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                        <Text fontSize="xs" color="gray.600">
                            Coincidencia exacta
                        </Text>
                    </Checkbox.Label>
                </Checkbox.Root>
            </Flex>
        </Box>
    );
};

export const BedsBathsPopoverBody = ({ draft, committed }) => (
    <Box>
        {ROWS.map(({ key, label: rowLabel }) => (
            <ChipRow
                key={key}
                label={rowLabel}
                draftKey={key}
                value={draft.drafts[key]}
                committed={committed[key]}
                exact={draft.exactMap[key]}
                onPickAny={draft.onPickAny}
                onPickN={draft.onPickN}
                onToggleExact={draft.onToggleExact}
            />
        ))}
    </Box>
);

const BedsBathsPopover = ({ state, setFilter, clearFilter }) => {
    const [open, setOpen] = useState(false);
    const draft = useBedsBathsDraft(state);
    const pillActive = ROWS.some((row) => state[row.key].length > 0);
    const label = buildPillLabel(state);

    const applyAll = () => {
        draft.commit(setFilter, clearFilter);
        setOpen(false);
    };
    const clearAll = () => {
        draft.reset(setFilter, clearFilter);
        setOpen(false);
    };

    return (
        <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
            <Popover.Trigger asChild>
                <Button
                    h="40px"
                    minW="180px"
                    px={4}
                    fontSize="sm"
                    fontWeight={500}
                    bg={pillActive ? "colorPalette.solid" : "white"}
                    color={pillActive ? "white" : "gray.700"}
                    borderWidth="1px"
                    borderColor={pillActive ? "colorPalette.solid" : "gray.200"}
                    borderRadius="md"
                    _hover={{ bg: pillActive ? "colorPalette.solid" : "gray.50" }}
                >
                    {label}
                    <Icon ml={2} boxSize={3} opacity={0.7}>
                        <CaretDownIcon weight="bold" />
                    </Icon>
                </Button>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content colorPalette="accent" w="360px" shadow="lg">
                        <Popover.Body p={4}>
                            <BedsBathsPopoverBody draft={draft} committed={state} />
                            <Flex
                                justify="space-between"
                                gap={2}
                                borderTopWidth="1px"
                                borderColor="gray.100"
                                pt={3}
                                mt={2}
                            >
                                <Button
                                    onClick={clearAll}
                                    variant="ghost"
                                    size="sm"
                                    px={5}
                                    borderRadius="md"
                                    color="gray.600"
                                >
                                    Limpiar todos
                                </Button>
                                <Button
                                    onClick={applyAll}
                                    size="sm"
                                    px={6}
                                    borderRadius="md"
                                    bg="colorPalette.solid"
                                    color="white"
                                    _hover={{ bg: "colorPalette.emphasized" }}
                                >
                                    Aplicar
                                </Button>
                            </Flex>
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};

export default BedsBathsPopover;
